import { apiClient } from './api';

export interface ClassworkItem {
  id: number;
  uid: string;
  title: string;
  description?: string | null;
  entry_type: 'classwork_digital' | 'classwork_penpaper' | 'classwork_offline' | 'oral' | 'practical' | 'other';
  status: 'draft' | 'active' | 'closed' | 'archived';
  seb_mode: 'seb_optional' | 'seb_required';
  parent_type: 'class' | 'course' | 'group' | 'ad_hoc';
  parent_id?: number | null;
  subject_id?: number | null;
  curriculum_id?: number | null;
  teaching_unit_id?: number | null;
  starts_at?: string | null;
  due_at?: string | null;
  latest_submit_at?: string | null;
  max_attempts: number;
  points_max?: number | null;
  grade_schema_id?: number | null;
  onlyoffice_enabled?: number | boolean;
  tiptap_enabled?: boolean;
  penpaper_enabled?: number | boolean;
  /** Schüler dürfen Bild/PDF als Datei abgeben (ohne Editor) */
  student_file_upload_enabled?: number | boolean;
  /** Erlaubte Dateitypen für Schüler-Dateiupload */
  student_upload_mode?: 'all_common' | 'images_pdf' | 'images_only' | 'pdf_only';
  /** Geschätzte Bearbeitungszeit in Minuten */
  estimated_duration_minutes?: number | null;
  expectation_horizon?: string | null;
  sample_solution?: string | null;
  created_at: string;
  updated_at: string;
}

export interface ClassworkAssignment {
  id: number;
  assignment_id?: number;
  classwork_id: number;
  student_id: string;
  first_name?: string | null;
  last_name?: string | null;
  class_name?: string | null;
  state: string;
  attempt_count: number;
  submitted_at?: string | null;
  graded_at?: string | null;
  published_at?: string | null;
  starts_at?: string | null;
  due_at?: string | null;
  latest_submit_at?: string | null;
  max_attempts?: number;
  points_max?: number | null;
  onlyoffice_enabled?: number | boolean;
  tiptap_enabled?: number | boolean;
  penpaper_enabled?: number | boolean;
  student_file_upload_enabled?: number | boolean;
  student_upload_mode?: 'all_common' | 'images_pdf' | 'images_only' | 'pdf_only';
  result_grade_value?: string | null;
  result_points_achieved?: number | null;
  result_points_max?: number | null;
  result_score_percent?: number | null;
  result_feedback?: string | null;
  result_explanation_text?: string | null;
  result_published_at?: string | null;
}

export interface ClassworkMaterial {
  id: number;
  classwork_id: number;
  material_type:
    | 'aufgabenstellung'
    | 'rueckgabe'
    | 'zusaetzlich'
    | 'erwartungshorizont'
    | 'musterloesung';
  file_source?: 'upload' | 'external' | 'dateien' | 'student_files';
  title: string;
  description?: string | null;
  mime_type?: string | null;
  original_name?: string | null;
  external_url?: string | null;
  is_required?: number | boolean;
}

export interface ClassworkReferenceVersion {
  id: number;
  classwork_id: number;
  reference_set_id: number;
  version_no: number;
  status: 'building' | 'ready' | 'active' | 'archived' | 'failed';
  is_active?: number | boolean;
  build_summary_json?: any;
  activated_at?: string | null;
  created_at: string;
}

export interface ClassworkStudentDirectoryEntry {
  sophomorixUnid?: string;
  Vorname?: string;
  Name?: string;
  Klasse?: string;
}

export async function fetchClassworkList(status?: string) {
  const response = await apiClient.get('/classwork', { params: { scope: 'mine', status: status || undefined } });
  return response.data?.data || [];
}

export async function createClasswork(payload: Partial<ClassworkItem> & { title: string; teaching_unit_id?: number | null }) {
  const response = await apiClient.post('/classwork', payload);
  return response.data?.data;
}

export async function updateClasswork(id: number, payload: Partial<ClassworkItem>) {
  const response = await apiClient.patch(`/classwork/${id}`, payload);
  return response.data?.data;
}

export async function deleteClasswork(id: number) {
  const response = await apiClient.delete(`/classwork/${id}`);
  return response.data?.success === true;
}

export async function duplicateClasswork(id: number) {
  const response = await apiClient.post(`/classwork/${id}/duplicate`);
  return response.data?.data;
}

export async function fetchClassworkDetail(id: number) {
  const response = await apiClient.get(`/classwork/${id}`);
  return response.data?.data;
}

export interface ClassworkAnalysisDiagnostics {
  classworkId: number;
  submissionStatus: Array<{ analysis_status: string; cnt: number }>;
  referenceStatus: Array<{ analysis_status: string; cnt: number }>;
  coverageBySource: Array<{ source_type: string; analysis_status: string; cnt: number }>;
  recentErrors: Array<{
    owner_type: string;
    owner_id: number;
    source_type: string;
    source_label?: string | null;
    error_message?: string | null;
    updated_at?: string | null;
  }>;
}

export interface ClassworkPipelineDecisionRow {
  id: number;
  classwork_id: number;
  assignment_id?: number | null;
  submission_id?: number | null;
  correction_id?: number | null;
  stage_name: string;
  decision_name: string;
  confidence?: number | null;
  reason_code: string;
  reason_text?: string | null;
  details_json?: any;
  created_at?: string;
  reasonMeta?: { label?: string; teacherHint?: string };
}

export interface ClassworkPipelineMetricRow {
  id: number;
  classwork_id: number;
  assignment_id?: number | null;
  submission_id?: number | null;
  correction_id?: number | null;
  stage_name: string;
  metric_key: string;
  metric_value?: number | null;
  metric_unit?: string | null;
  details_json?: any;
  created_at?: string;
}

export async function fetchClassworkAnalysisDiagnostics(classworkId: number): Promise<ClassworkAnalysisDiagnostics> {
  const response = await apiClient.get(`/classwork/${classworkId}/analysis-diagnostics`);
  return response.data?.data;
}

export async function fetchClassworkPipelineDecisions(classworkId: number, limit = 200): Promise<{
  classworkId: number;
  reasonCodeCatalog: Record<string, { label: string; teacherHint: string }>;
  items: ClassworkPipelineDecisionRow[];
}> {
  const response = await apiClient.get(`/classwork/${classworkId}/pipeline/decisions`, { params: { limit } });
  return response.data?.data || { classworkId, reasonCodeCatalog: {}, items: [] };
}

export async function fetchClassworkPipelineMetrics(classworkId: number, limit = 500): Promise<{
  classworkId: number;
  items: ClassworkPipelineMetricRow[];
  summary: Array<{ key: string; count: number; avg?: number | null; min?: number | null; max?: number | null }>;
}> {
  const response = await apiClient.get(`/classwork/${classworkId}/pipeline/metrics`, { params: { limit } });
  return response.data?.data || { classworkId, items: [], summary: [] };
}

export async function fetchClassworkPipelineReviewReasons(classworkId: number): Promise<{
  classworkId: number;
  items: Array<{
    reasonCode: string;
    count: number;
    explanation?: { label?: string; teacherHint?: string };
  }>;
  hubPurposeMatrix?: any;
}> {
  const response = await apiClient.get(`/classwork/${classworkId}/pipeline/review-reasons`);
  return response.data?.data || { classworkId, items: [] };
}

export interface ClassworkPipelineRegion {
  regionKey?: string | null;
  taskId?: string | null;
  regionType?: string | null;
  sourceRef?: string | null;
  anchorText?: string | null;
  confidence?: number | null;
  bbox?: any;
  details?: any;
}

export interface ClassworkPipelineCropResult {
  taskId: string;
  regionKey?: string | null;
  cropText?: string | null;
  confidence?: number | null;
  sourceRef?: string | null;
  details?: any;
}

export interface ClassworkPipelineReviewPage {
  sourceIndex: number;
  sourceLabel?: string | null;
  summary?: string | null;
  confidence?: number | null;
  regions: ClassworkPipelineRegion[];
  cropResults?: ClassworkPipelineCropResult[];
  templateRegions?: ClassworkPipelineRegion[];
  artifactId?: number;
  artifactType?: string;
  analysisStatus?: string;
  mimeType?: string;
  originalName?: string;
  viewUrl?: string;
  previewUrl?: string;
}

export interface ClassworkPipelineReviewSubmission {
  submissionId: number;
  attemptNo?: number | null;
  state?: string;
  source?: string;
  mimeType?: string;
  originalName?: string;
  analysisStatus?: string;
  viewUrl?: string;
  cropDiagnostics?: any;
  pages: ClassworkPipelineReviewPage[];
}

export interface ClassworkPipelineReviewData {
  classworkId: number;
  assignmentId: number;
  referenceSetVersionId?: number | null;
  referencePages: ClassworkPipelineReviewPage[];
  submissions: ClassworkPipelineReviewSubmission[];
}

export async function fetchClassworkPipelineReview(
  classworkId: number,
  assignmentId: number
): Promise<ClassworkPipelineReviewData> {
  const response = await apiClient.get(`/classwork/${classworkId}/pipeline/review/${assignmentId}`);
  return response.data?.data || {
    classworkId,
    assignmentId,
    referenceSetVersionId: null,
    referencePages: [],
    submissions: []
  };
}

export async function deleteClassworkMaterial(classworkId: number, materialId: number) {
  await apiClient.delete(`/classwork/${classworkId}/materials/${materialId}`);
}

export async function uploadClassworkMaterial(
  classworkId: number,
  payload: {
    file: File;
    materialType?:
      | 'aufgabenstellung'
      | 'rueckgabe'
      | 'zusaetzlich'
      | 'erwartungshorizont'
      | 'musterloesung';
    title?: string;
    description?: string;
    isRequired?: boolean;
  }
) {
  const formData = new FormData();
  formData.append('file', payload.file);
  if (payload.materialType) formData.append('material_type', payload.materialType);
  if (payload.title) formData.append('title', payload.title);
  if (payload.description) formData.append('description', payload.description);
  if (payload.isRequired) formData.append('is_required', '1');
  const response = await apiClient.post(`/classwork/${classworkId}/materials/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data?.data;
}

export async function createClassworkMaterialLink(
  classworkId: number,
  payload: {
    externalUrl: string;
    materialType?:
      | 'aufgabenstellung'
      | 'rueckgabe'
      | 'zusaetzlich'
      | 'erwartungshorizont'
      | 'musterloesung';
    title?: string;
    description?: string;
    isRequired?: boolean;
  }
) {
  const response = await apiClient.post(`/classwork/${classworkId}/materials/link`, {
    external_url: payload.externalUrl,
    material_type: payload.materialType || 'zusaetzlich',
    title: payload.title || undefined,
    description: payload.description || undefined,
    is_required: payload.isRequired ? 1 : 0
  });
  return response.data?.data;
}

export async function openClassworkMaterialOnlyofficeSession(classworkId: number, materialId: number) {
  const response = await apiClient.post(`/classwork/${classworkId}/materials/${materialId}/onlyoffice/session`);
  return response.data?.data;
}

export async function fetchJobStatus(jobId: string) {
  const response = await apiClient.get(`/notifications/job/${encodeURIComponent(jobId)}`);
  return response.data?.data;
}

export async function generateAssignments(classworkId: number, studentIds: string[]) {
  const response = await apiClient.post(`/classwork/${classworkId}/assignments/generate`, { student_ids: studentIds });
  return response.data?.data || [];
}

export async function resolveClassworkAssignmentTargets(
  classworkId: number,
  payload: {
    scope: 'class' | 'course' | 'group' | 'manual';
    class_name?: string;
    course_id?: number;
    group_id?: number;
    student_ids?: string[];
  }
) {
  const response = await apiClient.post(`/classwork/${classworkId}/assignments/resolve-targets`, payload);
  return response.data?.data;
}

export async function fetchClassworkStudentDirectory(): Promise<ClassworkStudentDirectoryEntry[]> {
  const response = await apiClient.get('/students');
  return response.data?.data || [];
}

export async function requestCompetencySuggestions(classworkId: number, payload: any) {
  const response = await apiClient.post(`/classwork/${classworkId}/competency-suggestions`, payload);
  return response.data?.data;
}

export async function confirmCompetencies(classworkId: number, payload: any) {
  const response = await apiClient.put(`/classwork/${classworkId}/competencies/confirm`, payload);
  return response.data?.data;
}

export async function fetchStudentClassworkAssignments() {
  const response = await apiClient.get('/student/classwork/student/assignments');
  return response.data?.data || [];
}

export async function fetchStudentAssignmentDetail(assignmentId: number) {
  const response = await apiClient.get(`/student/classwork/student/assignments/${assignmentId}/detail`);
  return response.data?.data;
}

export async function startStudentAssignment(assignmentId: number) {
  const response = await apiClient.post(`/student/classwork/student/assignments/${assignmentId}/start`);
  return response.data?.data;
}

export async function submitStudentAssignment(opts: {
  classworkId: number;
  assignmentId: number;
  file?: File;
  source?: 'upload' | 'onlyoffice' | 'scan' | 'tiptap';
  /** Idempotenz (empfohlen: crypto.randomUUID()) */
  requestId?: string;
  /** Pflicht bei seb_mode seb_required */
  attemptToken?: string;
}) {
  const formData = new FormData();
  if (opts.file) formData.append('file', opts.file);
  formData.append('source', opts.source || 'upload');
  if (opts.requestId) formData.append('request_id', opts.requestId);
  if (opts.attemptToken) formData.append('attemptToken', opts.attemptToken);
  const response = await apiClient.post(`/student/classwork/${opts.classworkId}/student/assignments/${opts.assignmentId}/submit`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data?.data;
}

export async function queueCorrection(classworkId: number, assignmentId: number) {
  if (!Number.isFinite(Number(assignmentId)) || Number(assignmentId) < 1) {
    throw new Error('assignment_id_missing');
  }
  const response = await apiClient.post(`/classwork/${classworkId}/corrections/${assignmentId}/run`);
  return response.data?.data;
}

export async function rerunCorrection(classworkId: number, assignmentId: number) {
  if (!Number.isFinite(Number(assignmentId)) || Number(assignmentId) < 1) {
    throw new Error('assignment_id_missing');
  }
  const response = await apiClient.post(`/classwork/${classworkId}/corrections/${assignmentId}/rerun`);
  return response.data?.data;
}

export async function restoreArchivedCorrection(classworkId: number, assignmentId: number, correctionId: number) {
  if (!Number.isFinite(Number(assignmentId)) || Number(assignmentId) < 1) {
    throw new Error('assignment_id_missing');
  }
  if (!Number.isFinite(Number(correctionId)) || Number(correctionId) < 1) {
    throw new Error('correction_id_missing');
  }
  const response = await apiClient.post(`/classwork/${classworkId}/corrections/${assignmentId}/${correctionId}/restore`);
  return response.data?.data;
}

/** Korrekturläufe zur Zuweisung (neueste zuerst), inkl. LLM-Felder */
export async function fetchClassworkCorrections(classworkId: number, assignmentId: number) {
  if (!Number.isFinite(Number(assignmentId)) || Number(assignmentId) < 1) {
    throw new Error('assignment_id_missing');
  }
  const response = await apiClient.get(`/classwork/${classworkId}/corrections/${assignmentId}`);
  return (response.data?.data || []) as ClassworkCorrectionRow[];
}

export async function fetchClassworkCorrectionDebug(
  classworkId: number,
  assignmentId: number,
  correctionId?: number
) {
  if (!Number.isFinite(Number(assignmentId)) || Number(assignmentId) < 1) {
    throw new Error('assignment_id_missing');
  }
  const response = await apiClient.get(`/classwork/${classworkId}/corrections/${assignmentId}/debug`, {
    params:
      correctionId != null && Number.isFinite(Number(correctionId)) && Number(correctionId) > 0
        ? { correctionId: Number(correctionId) }
        : undefined
  });
  return (response.data?.data || { corrections: [], debugLogsByCorrectionId: {} }) as {
    corrections: ClassworkCorrectionRow[];
    debugLogsByCorrectionId: Record<string, { correction?: any; student_friendly?: any }>;
  };
}

export interface ClassworkCorrectionRow {
  id: number;
  classwork_id: number;
  assignment_id: number;
  submission_id: number;
  correction_config_id?: number | null;
  reference_set_version_id?: number | null;
  status: string;
  model_name?: string | null;
  llm_response_json?: unknown;
  score_percent?: number | string | null;
  score_points?: number | string | null;
  strengths?: string | null;
  issues?: string | null;
  suggestions?: string | null;
  student_friendly_status?: 'idle' | 'queued' | 'processing' | 'completed' | 'failed' | string | null;
  student_friendly_text?: string | null;
  student_friendly_error?: string | null;
  student_friendly_completed_at?: string | null;
  uncertainty?: number | string | null;
  error_message?: string | null;
  queued_at?: string | null;
  completed_at?: string | null;
  is_archived?: number | boolean | null;
  archived_at?: string | null;
  archived_by?: number | null;
  archive_reason?: string | null;
}

export async function buildReferenceVersion(classworkId: number) {
  const response = await apiClient.post(`/classwork/${classworkId}/reference-versions/build`);
  return response.data?.data;
}

export async function cancelReferenceVersionBuild(classworkId: number) {
  const response = await apiClient.post(`/classwork/${classworkId}/reference-versions/build/cancel`);
  return response.data?.data;
}

export async function listReferenceVersions(classworkId: number) {
  const response = await apiClient.get(`/classwork/${classworkId}/reference-versions`);
  return response.data?.data || { versions: [], activeReferenceVersion: null };
}

export async function activateReferenceVersion(classworkId: number, versionId: number) {
  const response = await apiClient.post(`/classwork/${classworkId}/reference-versions/${versionId}/activate`);
  return response.data?.data;
}

export async function getReferenceVersionDetail(classworkId: number, versionId: number) {
  const response = await apiClient.get(`/classwork/${classworkId}/reference-versions/${versionId}`);
  return response.data?.data;
}

export interface RubricTaskDef {
  id: string;
  label: string;
  pointsMax: number;
  bonus: boolean;
  criteria?: string;
}

export interface RubricPayload {
  tasks: RubricTaskDef[];
  totalPointsBase: number;
  totalPointsWithBonus: number;
  source?: 'rubric_json' | 'llm_extraction' | 'llm_autogen_no_rubric' | null;
  extractedAt?: string | null;
  model?: string | null;
  notes?: string;
}

export interface RubricAcknowledgmentState {
  exists: boolean;
  status?: 'not_attempted' | 'none' | 'ready' | 'failed';
  source?: 'rubric_json' | 'llm_extraction' | 'llm_autogen_no_rubric' | null;
  hasTasks?: boolean;
  requiresAck?: boolean;
  acknowledged?: boolean;
  acknowledgedAt?: string | null;
  acknowledgedBy?: number | null;
  acknowledgedMeta?: { auto?: boolean; reason?: string; userName?: string; notes?: string } | null;
  blocksCorrection?: boolean;
}

export async function getReferenceVersionRubric(classworkId: number, versionId: number) {
  const response = await apiClient.get(
    `/classwork/${classworkId}/reference-versions/${versionId}/rubric`
  );
  const payload = response.data?.data || {};
  return {
    rubric: (payload.rubric as RubricPayload | null) || null,
    acknowledgment: (payload.acknowledgment as RubricAcknowledgmentState | null) || null
  };
}

export async function acknowledgeReferenceVersionRubric(
  classworkId: number,
  versionId: number,
  notes?: string
) {
  const response = await apiClient.post(
    `/classwork/${classworkId}/reference-versions/${versionId}/rubric/acknowledge`,
    notes ? { notes } : {}
  );
  return response.data?.data?.acknowledgment as RubricAcknowledgmentState;
}

export interface RubricArtifactDiagnostic {
  id: number;
  artifactType:
    | 'aufgabenstellung'
    | 'erwartungshorizont'
    | 'punkteliste'
    | 'musterloesung'
    | 'zusaetzlich'
    | string;
  sourceKind?: string | null;
  originalName?: string | null;
  mimeType?: string | null;
  parseStatus?: string | null;
  parseError?: string | null;
  extractedTextLength: number;
  ocrTextLength: number;
  extractedTextSnippet?: string | null;
  ocrTextSnippet?: string | null;
  hasVisionSummary: boolean;
  hasRubricJson: boolean;
  analysisStatus?: string | null;
}

export interface RubricLastAttemptDebug {
  extractor?: 'vision' | 'text' | string;
  mode?: 'extract_from_rubric' | 'autogen_no_rubric' | string;
  forcedAutogen?: boolean;
  model?: string | null;
  reason?: string | null;
  error?: string | null;
  errorCode?: string | null;
  rawResponseSnippet?: string | null;
  notes?: string | null;
  parsedTaskCount?: number | null;
  normalizedTaskCount?: number | null;
  artifactsSummary?: Array<{
    artifactId: number | null;
    artifactType: string | null;
    originalName: string | null;
    contentChars?: number;
    pagesSent?: number;
    bytesSent?: number;
    noUsablePages?: boolean;
  }>;
  inventoryErrors?: Array<{ artifactId: number | null; stage: string; error: string }>;
  /** Gesamtgröße der Bild-Buffer in der Vision-Anfrage (vor Base64). */
  totalBytes?: number | null;
  /** Geltende Payload-Limits dieses Versuchs. */
  limits?: { maxImages?: number; maxImageBytes?: number; maxTotalBytes?: number } | null;
  /** 0 = regulärer Versuch, 1 = automatisch mit reduzierter Payload nach 502/Body-Limit. */
  shrinkAttempt?: number;
  /** Dieser Versuch lief als Shrink-Retry nach hub_submit_failed_502. */
  retryFromBodyLimit?: boolean;
  /** Fehler des vorausgegangenen (abgebrochenen) Versuchs, kurz. */
  priorError?: string | null;
  pointsMaxHint?: number | null;
  attemptedAt?: string | null;
}

export interface RubricDiagnostics {
  exists: boolean;
  referenceSetVersionId?: number;
  classworkId?: number | null;
  versionStatus?: string | null;
  rubricStatus?: 'not_attempted' | 'none' | 'ready' | 'failed' | string;
  rubricError?: string | null;
  rubricSource?: 'rubric_json' | 'llm_extraction' | 'llm_autogen_no_rubric' | null;
  rubricTaskCount?: number;
  hasExplicitRubricMaterial?: boolean;
  anyUsableArtifactContent?: boolean;
  analysisPendingOrFailedCount?: number;
  artifactCounts?: Record<string, number>;
  artifacts?: RubricArtifactDiagnostic[];
  buildSummary?: any;
  createdAt?: string | null;
  updatedAt?: string | null;
  lastAttempt?: RubricLastAttemptDebug | null;
  attempts?: RubricLastAttemptDebug[];
  reason?:
    | 'ready'
    | 'no_reference_artifacts'
    | 'artifacts_have_no_content'
    | 'llm_error'
    | 'llm_unavailable'
    | 'llm_extracted_empty'
    | 'autogen_returned_empty'
    | 'not_attempted'
    | 'failed_other'
    | 'hub_body_or_upstream'
    | 'unknown'
    | string;
  recommendation?: string;
}

export async function getReferenceVersionRubricDiagnostics(
  classworkId: number,
  versionId: number
): Promise<RubricDiagnostics> {
  const response = await apiClient.get(
    `/classwork/${classworkId}/reference-versions/${versionId}/rubric/diagnostics`
  );
  return (response.data?.data?.diagnostics || { exists: false }) as RubricDiagnostics;
}

export async function rebuildReferenceVersionRubric(
  classworkId: number,
  versionId: number,
  opts?: { forceMode?: 'autogen' }
) {
  const response = await apiClient.post(
    `/classwork/${classworkId}/reference-versions/${versionId}/rubric/rebuild`,
    opts?.forceMode ? { forceMode: opts.forceMode } : {}
  );
  const payload = response.data?.data || {};
  return {
    rebuild: (payload.rebuild || null) as {
      status: 'ready' | 'none' | 'failed' | null;
      source: RubricPayload['source'];
      error: string | null;
      needsAcknowledgment: boolean;
    } | null,
    rubric: (payload.rubric as RubricPayload | null) || null,
    acknowledgment: (payload.acknowledgment as RubricAcknowledgmentState | null) || null,
    diagnostics: (payload.diagnostics as RubricDiagnostics | null) || null
  };
}

export async function finalizeGrade(classworkId: number, assignmentId: number, payload: any) {
  if (!Number.isFinite(Number(assignmentId)) || Number(assignmentId) < 1) {
    throw new Error('assignment_id_missing');
  }
  const response = await apiClient.put(`/classwork/${classworkId}/grades/${assignmentId}/finalize`, payload);
  return response.data?.data;
}

export async function publishGrade(classworkId: number, assignmentId: number, payload?: any) {
  if (!Number.isFinite(Number(assignmentId)) || Number(assignmentId) < 1) {
    throw new Error('assignment_id_missing');
  }
  const response = await apiClient.post(`/classwork/${classworkId}/grades/${assignmentId}/publish`, payload || {});
  return (response.data?.data || {}) as {
    published?: boolean;
    evidenceSync?: { created?: number; skipped?: number; removed?: number };
  };
}

export async function archiveSubmissionForResubmission(
  classworkId: number,
  assignmentId: number,
  payload?: { reason?: string }
) {
  if (!Number.isFinite(Number(assignmentId)) || Number(assignmentId) < 1) {
    throw new Error('assignment_id_missing');
  }
  const response = await apiClient.post(`/classwork/${classworkId}/assignments/${assignmentId}/archive-submission`, payload || {});
  return response.data?.data;
}

export async function restoreArchivedSubmission(classworkId: number, submissionId: number) {
  if (!Number.isFinite(Number(submissionId)) || Number(submissionId) < 1) {
    throw new Error('submission_id_missing');
  }
  const response = await apiClient.post(`/classwork/${classworkId}/submissions/${submissionId}/restore`, {});
  return response.data?.data;
}

export interface GradeSchemaBand {
  minPercent: number;
  grade: string;
  numeric?: number;
}

export interface GradeSchemaScale {
  bands: GradeSchemaBand[];
}

export interface GradeSchemaRow {
  id: number;
  name: string;
  scope_type: string;
  scope_id?: number | null;
  scale_type: string;
  scale_json: GradeSchemaScale | string;
  is_active?: number | boolean;
}

/** Aktive Schemas; mit includeInactive (nur Admin) auch archivierte */
export async function fetchGradeSchemas(opts?: { includeInactive?: boolean }) {
  const response = await apiClient.get('/classwork/grade-schemas', {
    params: opts?.includeInactive ? { include_inactive: '1' } : undefined
  });
  return (response.data?.data || []) as GradeSchemaRow[];
}

export async function fetchGradeSchemaSubjectDefaults(): Promise<
  Record<number, { grade_schema_id: number; subject_name?: string }>
> {
  const response = await apiClient.get('/classwork/grade-schemas/defaults');
  return response.data?.data || {};
}

export async function fetchDefaultGradeSchemaForSubject(subjectId: number) {
  const response = await apiClient.get(`/classwork/grade-schemas/default-for-subject/${subjectId}`);
  return response.data?.data as {
    grade_schema_id: number | null;
    subject_name?: string;
    schema: GradeSchemaRow | null;
  };
}

export async function createGradeSchema(payload: {
  name: string;
  scope_type?: string;
  scope_id?: number | null;
  scale_type?: string;
  scale_json: GradeSchemaScale;
}) {
  const response = await apiClient.post('/classwork/grade-schemas', payload);
  return response.data?.data as GradeSchemaRow;
}

export async function updateGradeSchema(
  schemaId: number,
  payload: Partial<{
    name: string;
    scope_type: string;
    scope_id: number | null;
    scale_type: string;
    scale_json: GradeSchemaScale;
    is_active: boolean;
  }>
) {
  const response = await apiClient.put(`/classwork/grade-schemas/${schemaId}`, payload);
  return response.data?.data as GradeSchemaRow;
}

export async function deleteGradeSchema(schemaId: number) {
  const response = await apiClient.delete(`/classwork/grade-schemas/${schemaId}`);
  return response.data?.data;
}

export async function setSubjectDefaultGradeSchema(subjectId: number, gradeSchemaId: number | null) {
  const response = await apiClient.put(`/classwork/grade-schemas/subject-defaults/${subjectId}`, {
    grade_schema_id: gradeSchemaId
  });
  return response.data?.data;
}

export async function previewGradeSchemaMatch(gradeSchemaId: number, scorePercent: number) {
  const response = await apiClient.post('/classwork/grade-schemas/match', {
    grade_schema_id: gradeSchemaId,
    score_percent: scorePercent
  });
  return response.data?.data;
}

/** OnlyOffice: Entwurfszeile anlegen (Backend löscht vorherigen onlyoffice-Draft derselben Zuweisung) */
export async function createStudentSubmissionDraft(assignmentId: number, source: 'onlyoffice' = 'onlyoffice') {
  const response = await apiClient.post(`/student/classwork/student/assignments/${assignmentId}/submissions/draft`, {
    source
  });
  return response.data?.data;
}

export async function openStudentOnlyofficeSession(assignmentId: number) {
  const response = await apiClient.post(`/student/classwork/student/assignments/${assignmentId}/onlyoffice/session`);
  return response.data?.data;
}

export async function finalizeStudentOnlyofficeSubmission(assignmentId: number) {
  const response = await apiClient.post(`/student/classwork/student/assignments/${assignmentId}/onlyoffice/finalize`);
  return response.data?.data;
}

export async function resetOnlyofficeRetry(classworkId: number, assignmentId: number) {
  const response = await apiClient.post(`/classwork/${classworkId}/assignments/${assignmentId}/onlyoffice/retry-reset`);
  return response.data?.data;
}

export async function importClassworkPenPaper(
  classworkId: number,
  payload: {
    rows: Record<string, unknown>[];
    mode?: 'skip_duplicates' | 'update_existing' | 'fail_on_conflict';
    import_batch_id?: string;
  }
) {
  const response = await apiClient.post(`/classwork/${classworkId}/penpaper/import`, payload);
  return response.data?.data;
}

/** Neuer neutraler Name (Lehrer-Upload/Offline-Import), alter Endpoint bleibt via Alias erreichbar. */
export async function importClassworkTeacherUploadRows(
  classworkId: number,
  payload: {
    rows: Record<string, unknown>[];
    mode?: 'skip_duplicates' | 'update_existing' | 'fail_on_conflict';
    import_batch_id?: string;
  }
) {
  const response = await apiClient.post(`/classwork/${classworkId}/teacher-upload/import`, payload);
  return response.data?.data;
}

export async function uploadClassworkPenPaperScan(classworkId: number, studentId: string, file: File) {
  const formData = new FormData();
  formData.append('student_id', studentId);
  formData.append('file', file);
  const response = await apiClient.post(`/classwork/${classworkId}/penpaper/scans/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data?.data;
}

export async function uploadClassworkTeacherScan(classworkId: number, studentId: string, file: File) {
  const formData = new FormData();
  formData.append('student_id', studentId);
  formData.append('file', file);
  const response = await apiClient.post(`/classwork/${classworkId}/teacher-upload/scans/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data?.data;
}

export async function uploadClassworkTeacherScansBulk(classworkId: number, files: File[]) {
  const formData = new FormData();
  for (const file of files || []) {
    formData.append('files', file);
  }
  const response = await apiClient.post(`/classwork/${classworkId}/teacher-upload/scans/upload-bulk`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data?.data;
}

export type PenPaperBulkStrategy = 'ocr_first_manual_fallback' | 'marker_qr_required';
export type TeacherUploadBatchStrategy = PenPaperBulkStrategy;

export type PenPaperBulkSegment = {
  id: number;
  run_id: number;
  segment_no: number;
  page_from: number;
  page_to: number;
  page_count: number;
  suggested_student_id?: string | null;
  confidence?: number | null;
  final_student_id?: string | null;
  status: 'pending' | 'mapped' | 'finalized' | 'failed';
  notes?: string | null;
};

export async function startPenPaperBulkImport(classworkId: number, strategy: PenPaperBulkStrategy, file: File) {
  const formData = new FormData();
  formData.append('strategy', strategy);
  formData.append('file', file);
  const response = await apiClient.post(`/classwork/${classworkId}/penpaper/bulk-import/start`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data?.data;
}

export async function startClassworkTeacherBatchImport(classworkId: number, strategy: TeacherUploadBatchStrategy, file: File) {
  const formData = new FormData();
  formData.append('strategy', strategy);
  formData.append('file', file);
  const response = await apiClient.post(`/classwork/${classworkId}/teacher-upload/batch-import/start`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data?.data;
}

export async function fetchPenPaperBulkImportRun(classworkId: number, runId: number) {
  const response = await apiClient.get(`/classwork/${classworkId}/penpaper/bulk-import/${runId}`);
  return response.data?.data;
}

export async function fetchClassworkTeacherBatchImportRun(classworkId: number, runId: number) {
  const response = await apiClient.get(`/classwork/${classworkId}/teacher-upload/batch-import/${runId}`);
  return response.data?.data;
}

export async function updatePenPaperBulkImportSegments(
  classworkId: number,
  runId: number,
  segments: Array<{
    segment_no: number;
    final_student_id?: string | null;
    page_from?: number;
    page_to?: number;
  }>
) {
  const response = await apiClient.put(`/classwork/${classworkId}/penpaper/bulk-import/${runId}/segments`, { segments });
  return response.data?.data;
}

export async function updateClassworkTeacherBatchImportSegments(
  classworkId: number,
  runId: number,
  segments: Array<{
    segment_no: number;
    final_student_id?: string | null;
    page_from?: number;
    page_to?: number;
  }>
) {
  const response = await apiClient.put(`/classwork/${classworkId}/teacher-upload/batch-import/${runId}/segments`, { segments });
  return response.data?.data;
}

export async function finalizePenPaperBulkImport(classworkId: number, runId: number) {
  const response = await apiClient.post(`/classwork/${classworkId}/penpaper/bulk-import/${runId}/finalize`);
  return response.data?.data;
}

export async function finalizeClassworkTeacherBatchImport(classworkId: number, runId: number) {
  const response = await apiClient.post(`/classwork/${classworkId}/teacher-upload/batch-import/${runId}/finalize`);
  return response.data?.data;
}

export async function downloadPenPaperPrintSheets(
  classworkId: number,
  payload: { layout: 'grid' | 'lines' | 'blank'; assignment_ids?: number[] }
) {
  const response = await apiClient.post(`/classwork/${classworkId}/penpaper/print-sheets`, payload, {
    responseType: 'blob'
  });
  return response.data as Blob;
}

export async function downloadClassworkPrintSheets(
  classworkId: number,
  payload: { layout: 'grid' | 'lines' | 'blank'; assignment_ids?: number[] }
) {
  const response = await apiClient.post(`/classwork/${classworkId}/teacher-upload/print-sheets`, payload, {
    responseType: 'blob'
  });
  return response.data as Blob;
}

export type ClassworkResultsDocType =
  | 'overview_rough'
  | 'overview_matrix'
  | 'student_summary'
  | 'student_detail';

export interface ClassworkResultsExportResponse {
  blob: Blob;
  filename: string;
  mime: string;
}

export async function downloadClassworkResultsExport(
  classworkId: number,
  payload: { documents: ClassworkResultsDocType[]; assignment_ids?: number[] }
): Promise<ClassworkResultsExportResponse> {
  const response = await apiClient.post(
    `/classwork/${classworkId}/results-export`,
    payload,
    { responseType: 'blob' }
  );
  const cd = String(response.headers?.['content-disposition'] || '');
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
  const mime = String(response.headers?.['content-type'] || 'application/pdf');
  const fallbackExt = mime.includes('zip') ? 'zip' : 'pdf';
  return {
    blob: response.data as Blob,
    filename: match?.[1] || `klassenarbeit-${classworkId}-ergebnisse.${fallbackExt}`,
    mime
  };
}

export async function uploadStudentPenpaperPage(assignmentId: number, file: File, pageNo?: number) {
  const formData = new FormData();
  formData.append('file', file);
  if (pageNo != null) formData.append('page_no', String(pageNo));
  const response = await apiClient.post(`/student/classwork/student/assignments/${assignmentId}/penpaper/pages`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data?.data;
}

export async function uploadStudentOfflinePage(assignmentId: number, file: File, pageNo?: number) {
  const formData = new FormData();
  formData.append('file', file);
  if (pageNo != null) formData.append('page_no', String(pageNo));
  const response = await apiClient.post(`/student/classwork/student/assignments/${assignmentId}/offline/pages`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data?.data;
}

export async function fetchStudentPenpaperPages(assignmentId: number) {
  const response = await apiClient.get(`/student/classwork/student/assignments/${assignmentId}/penpaper/pages`);
  return response.data?.data;
}

export async function fetchStudentOfflinePages(assignmentId: number) {
  const response = await apiClient.get(`/student/classwork/student/assignments/${assignmentId}/offline/pages`);
  return response.data?.data;
}

export async function deleteStudentPenpaperPage(assignmentId: number, pageNo: number) {
  const response = await apiClient.delete(`/student/classwork/student/assignments/${assignmentId}/penpaper/pages/${pageNo}`);
  return response.data?.data;
}

export async function deleteStudentOfflinePage(assignmentId: number, pageNo: number) {
  const response = await apiClient.delete(`/student/classwork/student/assignments/${assignmentId}/offline/pages/${pageNo}`);
  return response.data?.data;
}

export async function finalizeStudentPenpaperSubmission(assignmentId: number) {
  const response = await apiClient.post(`/student/classwork/student/assignments/${assignmentId}/penpaper/finalize`);
  return response.data?.data;
}

export async function finalizeStudentOfflineSubmission(assignmentId: number) {
  const response = await apiClient.post(`/student/classwork/student/assignments/${assignmentId}/offline/finalize`);
  return response.data?.data;
}

export async function fetchClassworkOnlyofficeContext(classworkId: number) {
  const response = await apiClient.get(`/classwork/${classworkId}/onlyoffice/context`);
  return response.data?.data;
}
